/**
 * Acting-tenant state for the frontend.
 *
 * A user on an admin role may act on one operating bakery at a time. The
 * selection is sent to Hive as the `x-account-id` header on REST calls and as
 * `auth.accountId` on the socket handshake; Hive re-derives the role from the
 * database and only honours the value for an admin role, so a non-admin cannot
 * use it to reach another bakery's data.
 *
 * Kept outside React state so the plain API clients (which are module
 * singletons, not hooks) can read it synchronously on every request.
 */

/**
 * `_UserRole.ID`s allowed to switch tenants: System Admin (1), Admin (2),
 * MES (41). Holding one grants cross-tenant access from any account.
 *
 * Must stay in step with TENANT_ADMIN_ROLE_IDS in Hive's tenantMiddleware.
 * This copy only decides whether to RENDER the selector -- Hive re-checks the
 * role on every request from the database, so a tampered frontend gains
 * nothing but a control that does not work.
 *
 * An explicit list, not a threshold: `_UserRole` ids are creation order
 * (Radnik=31, Skladistar=40, MES=41), so "role <= n" would be meaningless.
 */
export const TENANT_ADMIN_ROLE_IDS = [1, 2, 41];

const ACTING_ACCOUNT_KEY = 'actingAccountId';

export const ACTING_ACCOUNT_HEADER = 'x-account-id';

/** Fired when the acting tenant changes so views can refetch. */
export const TENANT_CHANGED_EVENT = 'poweriot:tenant-changed';

function readStoredUser(): any | null {
    try {
        const raw = localStorage.getItem('user');
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

/** The account the logged-in user belongs to (their home account). */
export function getHomeAccountId(): number | null {
    const stored = readStoredUser();
    const raw = stored?.user?.accountId ?? stored?.accountId;
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

/** The logged-in user's role id, as returned by /login. */
export function getUserRoleId(): number | null {
    const stored = readStoredUser();
    const raw =
        stored?.user?.userRoleId ??
        stored?.userRoleId ??
        stored?.user?.UserRoleID ??
        stored?.UserRoleID;
    if (raw == null) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
}

export function isTenantAdminRole(roleId: number | null): boolean {
    return roleId != null && TENANT_ADMIN_ROLE_IDS.includes(Number(roleId));
}

/**
 * May this user switch bakeries and see the selector?
 *
 * Role alone, regardless of account: a Boogie EU Admin may act on Boogie US and
 * vice versa. Every non-admin role -- including REARM staff on Radnik -- is
 * confined to its own tenant.
 */
export function canSwitchTenant(): boolean {
    return isTenantAdminRole(getUserRoleId());
}

/**
 * The tenant whose data should be shown. For anyone who may not switch this is
 * always their own account regardless of what is stored, so a stale key left
 * behind by a previous admin session cannot leak data.
 */
export function getActingAccountId(): number | null {
    const home = getHomeAccountId();
    if (home == null) return null;
    if (!canSwitchTenant()) return home;

    const raw = localStorage.getItem(ACTING_ACCOUNT_KEY);
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

export function setActingAccountId(accountId: number | null): void {
    if (!canSwitchTenant()) return;
    try {
        if (accountId == null) localStorage.removeItem(ACTING_ACCOUNT_KEY);
        else localStorage.setItem(ACTING_ACCOUNT_KEY, String(accountId));
    } catch {
        /* storage unavailable; selection is best-effort */
    }
    window.dispatchEvent(new CustomEvent(TENANT_CHANGED_EVENT, { detail: { accountId } }));
}

export function clearActingAccountId(): void {
    try {
        localStorage.removeItem(ACTING_ACCOUNT_KEY);
    } catch {
        /* noop */
    }
}

/**
 * Header to merge into REST requests. Empty for anyone who may not switch, and
 * for an admin who has not picked a bakery yet (Hive then scopes to their own
 * account).
 */
export function tenantHeaders(): Record<string, string> {
    if (!canSwitchTenant()) return {};
    const acting = getActingAccountId();
    return acting != null ? { [ACTING_ACCOUNT_HEADER]: String(acting) } : {};
}

/** Extra socket handshake auth fields carrying the acting tenant. */
export function tenantSocketAuth(): Record<string, number> {
    if (!canSwitchTenant()) return {};
    const acting = getActingAccountId();
    return acting != null ? { accountId: acting } : {};
}
